import type {ReactElement} from 'react'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import type {IssueCommentPinnedEvent$key} from './__generated__/IssueCommentPinnedEvent.graphql'
import type {IssueCommentUnpinnedEvent$key} from './__generated__/IssueCommentUnpinnedEvent.graphql'
import {issueCommentPinnedEventFragment, PinnedCommentLink, PinnedCommentRendering} from './IssueCommentPinnedEvent'
import {
  issueCommentUnpinnedEventFragment,
  UnpinnedCommentLink,
  UnpinnedCommentRendering,
} from './IssueCommentUnpinnedEvent'

type RolledupIssueCommentPinEventProps = {
  rollupGroup: Record<string, Array<IssueCommentPinnedEvent$key | IssueCommentUnpinnedEvent$key>>
}

export function RolledupIssueCommentPinEvent({rollupGroup}: RolledupIssueCommentPinEventProps): ReactElement {
  const pinnedEvents = (rollupGroup.IssueCommentPinnedEvent as IssueCommentPinnedEvent$key[]) || []
  const unpinnedEvents = (rollupGroup.IssueCommentUnpinnedEvent as IssueCommentUnpinnedEvent$key[]) || []
  const firstPinnedRef = pinnedEvents[0] ?? null
  const firstUnpinnedRef = unpinnedEvents[0] ?? null

  const firstPinnedData = useFragment(issueCommentPinnedEventFragment, firstPinnedRef)
  const firstUnpinnedData = useFragment(issueCommentUnpinnedEventFragment, firstUnpinnedRef)
  const pinnedTimestamp = parseTimestamp(firstPinnedData?.createdAt)
  const unpinnedTimestamp = parseTimestamp(firstUnpinnedData?.createdAt)

  const isSingleCommentPinAndUnpin =
    pinnedEvents.length === 1 &&
    unpinnedEvents.length === 1 &&
    firstPinnedData?.issueComment?.databaseId != null &&
    firstPinnedData.issueComment.databaseId === firstUnpinnedData?.issueComment?.databaseId

  if (isSingleCommentPinAndUnpin) {
    const pinnedOccursFirst =
      pinnedTimestamp !== null && unpinnedTimestamp !== null ? pinnedTimestamp <= unpinnedTimestamp : true
    const firstLeading = pinnedOccursFirst
      ? LABELS.timeline.issueCommentPinned.leading
      : LABELS.timeline.issueCommentUnpinned.leading
    const secondLeading = pinnedOccursFirst
      ? LABELS.timeline.issueCommentUnpinned.leading
      : LABELS.timeline.issueCommentPinned.leading
    const combinedCommentLink = pinnedEvents[0] ? (
      <PinnedCommentLink queryRef={pinnedEvents[0]} />
    ) : unpinnedEvents[0] ? (
      <UnpinnedCommentLink queryRef={unpinnedEvents[0]} />
    ) : (
      <span>{`a ${LABELS.timeline.issueCommentPinned.comment}`}</span>
    )

    return (
      <>
        {firstLeading} {LABELS.timeline.and} {secondLeading} {'a '}
        {combinedCommentLink}
      </>
    )
  }

  return (
    <>
      <PinnedCommentRendering queryRefs={pinnedEvents} rollup />
      {pinnedEvents.length > 0 && unpinnedEvents.length > 0 && ` ${LABELS.timeline.and} `}
      <UnpinnedCommentRendering queryRefs={unpinnedEvents} rollup />
    </>
  )
}

const parseTimestamp = (value?: string | null): number | null => {
  if (!value) {
    return null
  }

  const time = Date.parse(value)
  return Number.isNaN(time) ? null : time
}
